import {
  useState, useEffect, useCallback, useRef, useLayoutEffect,
} from 'react';
import { useLocation } from 'react-router-dom';

import {
  REGISTER_OPTIN_PARAM,
  REGISTER_SIGNUP_PARAM,
  ROUTE_REWARDS_V2,
  SUBSCRIPTION_FROM_CHECKOUT_PARAM,
  SUBSCRIPTION_ORDER_ITEMS_PARAM,
} from '../../../../config/routes';
import { Button, BUTTON_TYPE, BUTTON_SIZE } from '../../../../global/Button';
import GlobalLogin, { viewType } from '../../../../global/GlobalLogin';
import LoadingBar from '../../../../global/LoadingBar';
import SubscriptionSelectionSections, { SUBSCRIPTION_SELECTION_SECTIONS } from './SubscriptionSelectionSections';
import { getDecryptedData } from '../../../../utils/crypto-utils';
import { finishProcess } from '../../../../utils/login';
import { useFirebaseAuth } from '../../../../../global/FirebaseProvider/FirebaseProvider';
import { useSubscription } from '../../SubscriptionProvider';
import {
  setSubscriptionOrderItemsInStorage,
  SUBSCRIPTION_PAYMENT_TYPES,
  SUBSCRIPTION_STEPS,
} from '../../../../utils/subscriptions';
import {
  POSTHOG_PROPERTIES,
  SUBSCRIPTION_TRACKER_TYPES,
  TRACKER_IDS,
  getTrackerIdEventName,
} from '../../../../config/tracker';
import { scrollSubscriptionSectionIntoView } from '../../../../utils/subscriptionFlow';
import useNavigateWithSearch from '../../../../../../hooks/useNavigateWithSearch';
import { pushSubscriptionStartedEvent } from '../../../../utils/gtm';
import {
  SubscriptionHero,
  SubscriptionStepCard,
  SubscriptionLegal,
} from '../../shared';

import './SubscriptionSelection.scss';

const {
  subscription: {
    selection: {
      [SUBSCRIPTION_TRACKER_TYPES.VIEW]: trackerView,
      [SUBSCRIPTION_TRACKER_TYPES.SUBMIT]: trackerSubmit,
    },
  },
} = TRACKER_IDS;

const SubscriptionSelection = () => {
  const stepSectionRef = useRef(null);
  const selectionScrollPassInitialRef = useRef(true);
  const timeoutRef = useRef(null);
  const { search } = useLocation();
  const {
    isSignedIn,
    isLoading: isLoadingFirebaseAuth,
    userData,
    signOut,
  } = useFirebaseAuth();

  const {
    config: subscriptionConfig,
    isFromCheckout,
    isAnonymous,
    setStep,
    orderItems,
    optionSelected,
    setOptionSelected,
    posthog,
    contentScrollRef,
    selectionScrollReentryCount,
  } = useSubscription();
  const navigateWithSearch = useNavigateWithSearch();

  const [emailLinkSent, setEmailLinkSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [finishProcessData, setFinishProcessData] = useState({});
  const [view, setView] = useState(viewType.VIEW_EMAIL_FORM);
  const [error, setError] = useState(null);

  const {
    steps = {},
  } = subscriptionConfig;

  const handleSelection = useCallback((uid, type) => {
    if (!uid || !type || uid === optionSelected) {
      return;
    }
    setOptionSelected(uid);
  }, [optionSelected]);

  const handleContinueWithType = useCallback((type) => {
    posthog.capture(getTrackerIdEventName(trackerSubmit));

    if (type === SUBSCRIPTION_PAYMENT_TYPES.SUBSCRIPTION) {
      pushSubscriptionStartedEvent(userData);
    }

    setStep(SUBSCRIPTION_STEPS.PAYMENT);
  }, [userData, posthog, setStep]);

  const handleOnEmailLinkSent = () => {
    setEmailLinkSent(true);
  };

  const handleChangeAccount = useCallback(async () => {
    setIsSigningOut(true);
    await signOut();
    setView(viewType.VIEW_EMAIL_FORM);
  }, [signOut]);

  const redirect = async () => {
    setIsLoading(true);

    const {
      error: userError,
      uid: userUid,
    } = userData;

    if (userError) {
      setError(userError);
      setIsLoading(false);
      return;
    }

    if (emailLinkSent) {
      navigateWithSearch(ROUTE_REWARDS_V2);
      return;
    }

    const query = new URLSearchParams(search);
    const data = await getDecryptedData(query, userUid);
    await finishProcess({
      ...finishProcessData,
      ...(REGISTER_SIGNUP_PARAM in data && {
        userRegistered: data[REGISTER_SIGNUP_PARAM],
      }),
      ...(REGISTER_OPTIN_PARAM in data && {
        optIn: data[REGISTER_OPTIN_PARAM],
      }),
      userData,
      search,
      posthog,
      trackerProps: {
        [POSTHOG_PROPERTIES.FLOW]: 'subscription',
      },
    });

    if (data[SUBSCRIPTION_ORDER_ITEMS_PARAM]) {
      setSubscriptionOrderItemsInStorage(data[SUBSCRIPTION_ORDER_ITEMS_PARAM]);
    }

    if (isFromCheckout || data[SUBSCRIPTION_FROM_CHECKOUT_PARAM] === 'true') {
      setStep(SUBSCRIPTION_STEPS.CONFIRMATION);
    }

    setView(viewType.VIEW_EMAIL_FORM);
    setIsLoading(false);
  };

  useEffect(() => {
    if (!isLoadingFirebaseAuth && isSignedIn && userData?.email) {
      redirect();
    }
  }, [isSignedIn, userData, isLoadingFirebaseAuth]);

  useEffect(() => {
    if (isSigningOut) {
      timeoutRef.current = setTimeout(() => {
        setIsSigningOut(false);
      }, 2000);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isSigningOut]);

  useEffect(() => {
    posthog.capture(getTrackerIdEventName(trackerView));
  }, []);

  const selectionContentStageKey = [
    view,
    emailLinkSent,
    isSigningOut,
    isSignedIn,
    userData?.email ?? '',
    error ?? '',
  ].join('|');

  useLayoutEffect(() => {
    const run = () => {
      scrollSubscriptionSectionIntoView(contentScrollRef.current, stepSectionRef.current);
    };
    const schedule = () => {
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(run);
      });
      return () => cancelAnimationFrame(id);
    };

    if (selectionScrollPassInitialRef.current) {
      selectionScrollPassInitialRef.current = false;
      if (selectionScrollReentryCount === 0) {
        return undefined;
      }
      return schedule();
    }

    return schedule();
  }, [
    contentScrollRef,
    selectionScrollReentryCount,
    selectionContentStageKey,
  ]);

  return (
    <div className="SubscriptionSelection">
      <SubscriptionHero />
      <div ref={stepSectionRef} className="SubscriptionSelection__content">
        <SubscriptionStepCard
          stepNumber="1"
          title="Choose Your Clean Out"
          description="Get massive savings with Trashie Unlimited — unlimited bags, tech recycling, and rewards."
          className="SubscriptionSelection__step"
        >
          <SubscriptionSelectionSections
            sections={(steps?.[SUBSCRIPTION_STEPS.SELECTION]?.sections || []).filter(
              (section) => section.id !== SUBSCRIPTION_SELECTION_SECTIONS.STARTER_KIT,
            )}
            optionSelected={optionSelected}
            onOptionSelect={handleSelection}
            onContinue={handleContinueWithType}
          />
          {error && (
            <div className="SubscriptionSelection__content-error">
              {error}
            </div>
          )}
          {!isAnonymous && (
            <>
              {isSigningOut && (
                <LoadingBar className="SubscriptionSelection__content-loading" />
              )}
              {!isSigningOut && (
                <div className="SubscriptionSelection__content-login">
                  <div className="SubscriptionSelection__content-login-title">
                    {steps?.login?.title}
                  </div>
                  <div className="SubscriptionSelection__content-login-container">
                    <div className="SubscriptionSelection__content-login-content">
                      {isSignedIn && userData?.email ? (
                        <>
                          <span className="SubscriptionSelection__content-login-text">You are signed in as:</span>
                          <span className="SubscriptionSelection__content-login-email">{userData?.email}</span>
                          <Button
                            type={BUTTON_TYPE.LINK_SEPTENARY}
                            size={BUTTON_SIZE.LARGE}
                            className="SubscriptionSelection__content-login-signOut"
                            onClick={handleChangeAccount}
                          >
                            Sign in as a different account
                          </Button>
                        </>
                      ) : (
                        <GlobalLogin
                          isParentLoading={isLoading || isLoadingFirebaseAuth}
                          emailLinkParams={{
                            [SUBSCRIPTION_FROM_CHECKOUT_PARAM]: isFromCheckout,
                            [SUBSCRIPTION_ORDER_ITEMS_PARAM]: orderItems,
                          }}
                          onEmailLinkSent={handleOnEmailLinkSent}
                          setFinishProcessData={setFinishProcessData}
                          view={view}
                          setView={setView}
                          isEmbedded
                          showLogo={false}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </SubscriptionStepCard>
      </div>
      <SubscriptionLegal />
    </div>
  );
};

export default SubscriptionSelection;
